import React, { useMemo } from 'react';

export default function SentimentCharts({
  sentiment = 0,
  totalSentiment = 5,
  bgColor = 'rgba(50,111,240,0.1)',
  color = '#326FF0',
  strokeWidth = 16,
  width = 100,
  height = 50,
}: {
  sentiment: number;
  totalSentiment?: number;
  bgColor?: string;
  color?: string;
  strokeWidth?: number;
  width?: number;
  height?: number;
}) {
  const getCircleCommon = (w: number, h: number, strokeWidth: number) => {
    const half = strokeWidth / 2;
    const hR = h - half;
    const wR = (w - half) / 2;
    const r = Math.min(hR, wR) - 4;

    const x = w / 2;
    const y = h + half;
    return {
      x,
      y,
      r,
    };
  };

  const getStartEndPointer = ({
    x,
    y,
    r,
  }: {
    x: number;
    y: number;
    r: number;
  }) => {
    const beginPointer = { x: x - r, y: y };
    const endPointer = { x: x + r, y: y };

    return {
      beginPointer,
      endPointer,
    };
  };

  const { d, dashWidth, x, y, r, beginPointer } = useMemo(() => {
    const { x, y, r } = getCircleCommon(width, height, strokeWidth);
    const { beginPointer, endPointer } = getStartEndPointer({ x, y, r });
    const d = `M ${beginPointer.x} ${beginPointer.y} A ${r} ${r} 0 0 1 ${endPointer.x} ${endPointer.y}`;
    const dashWidth = Math.ceil(Math.PI * r);

    return {
      d: d,
      dashWidth,
      x,
      y,
      r,
      beginPointer,
    };
  }, [width, height, strokeWidth]);

  const { strokeDashoffset, proportionDeg } = useMemo(() => {
    let value = sentiment / totalSentiment;
    value = 1 - (value > 1 ? 1 : value);

    return {
      strokeDashoffset: dashWidth * value,
      proportionDeg: 1 - value,
    };
  }, [dashWidth, sentiment]);

  // 计算斜线的相关参数
  const { from, to, strokeDash, ...lineSentiment } = useMemo(() => {
    const center = x + ' ' + y;
    const deg = 180 * proportionDeg;

    return {
      x1: beginPointer.x - strokeWidth / 2 - 2,
      y1: beginPointer.y,
      x2: x,
      y2: y,
      transform: `rotate(${deg} ${center})`,
      from: `${0} ${center}`,
      to: `${deg} ${center}`,
      strokeDash: strokeWidth + 4,
    };
  }, [x, y, beginPointer, proportionDeg]);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      width={width}
      height={height + 10 + strokeWidth / 2}
    >
      <path
        d={d}
        stroke={bgColor}
        strokeWidth={strokeWidth}
        fill="none"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={d}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={`${dashWidth} ${dashWidth}`}
        strokeDashoffset={strokeDashoffset}
        fill="none"
        vectorEffect="non-scaling-stroke"
      >
        <animate
          attributeType="XML"
          attributeName="stroke-dashoffset"
          from={dashWidth}
          to={strokeDashoffset}
          dur="1s"
          calcMode="paced"
          repeatCount="1"
        />
      </path>

      <line
        {...lineSentiment}
        strokeDasharray={`${strokeDash} ${r}`}
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from={from}
          to={to}
          dur="1s"
          calcMode="paced"
          repeatCount="1"
        />
      </line>
      <text
        style={{
          fontSize: 20,
          fontWeight: 700,
          fontFamily: 'num-b',
        }}
        x={x}
        textAnchor="middle"
        y={y + 5}
      >
        {sentiment}
      </text>
    </svg>
  );
}
